// Shifts Domain Types
// Generated from DataModel.sql shifts schema

import type {
  Address,
  BaseEntity,
  DateRange,
  Money,
  RecommendationRequestStatus,
  TimeSlot,
  UserDocument,
  WeekDay
} from "./core";
import type { Practice, PracticeBranch } from "./practices";
import type { Skill, Specialty } from "./professionals";
import {
  ActiveIcon,
  CheckBoldIcon,
  CrossBoldIcon,
  EditDraftBoldIcon,
  TrashIcon,
  UrgentIcon
} from "@/utilities/icon-utils";

// Shift Enums
export type ShiftTimeType = "recurring" | "custom";

export type ShiftPricingType = "fixed_amount" | "working_hours" | "total_hours";

/** Default for practice post-shift until the form exposes pricing mode selection. */
export const DEFAULT_POST_SHIFT_PRICING_TYPE: ShiftPricingType =
  "working_hours";

export type ShiftStatus =
  | "draft"
  | "upcoming"
  | "completed"
  | "cancelled"
  | "deleted"
  | "active";

export type ShiftPostStatus =
  | "draft"
  | "posted"
  | "partially_assigned"
  | "fully_assigned"
  | "completed"
  | "cancelled"
  | "deleted"
  | "active";

/** Label + Tailwind classes for shift post status badges (shared across practice UI). */
export type ShiftPostStatusBadgeStyle = {
  label: string;
  className: string;
};

export const SHIFT_POST_STATUS_BADGE: Record<
  ShiftPostStatus,
  ShiftPostStatusBadgeStyle
> = {
  draft: { label: "Draft", className: "bg-muted text-muted-foreground" },
  posted: {
    label: "Posted",
    className:
      "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200"
  },
  partially_assigned: {
    label: "Partial",
    className: "bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200"
  },
  fully_assigned: {
    label: "Assigned",
    className:
      "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200"
  },
  active: {
    label: "Active",
    className: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200"
  },
  completed: {
    label: "Completed",
    className:
      "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200"
  },
  cancelled: {
    label: "Cancelled",
    className: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200"
  },
  deleted: {
    label: "Deleted",
    className: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200"
  }
};

/** All {@link ShiftStatus} values for parsing API string fields. */
export const SHIFT_STATUS_VALUES: readonly ShiftStatus[] = [
  "draft",
  "upcoming",
  "active",
  "completed",
  "cancelled",
  "deleted"
];

export type ApplicationStatus =
  | "draft"
  | "applied"
  | "nominated"
  | "not_nominated"
  | "withdrawn"
  | "selected";

export type ShiftListSortBy =
  | "Title"
  | "CreatedAt"
  | "UpdatedAt"
  | "CompletedAt"
  | "CancelledAt"
  | "ShiftTimeType";

export type ShiftListFilters = {
  // Practice branch filter
  practiceBranchId: string;
  // Date filter
  startDate?: string;
  endDate?: string;
  // Rate filter
  minHourlyRate?: number;
  maxHourlyRate?: number;
  // Specialty filter
  specialties?: string[];
};

// Shift Template Entities
export interface ShiftTemplate extends BaseEntity {
  practiceId: string;
  practiceBranchId: string;
  title: string;
  description: string;
  specialtyId: string;
  shiftTimeType: ShiftTimeType;
  recurringDateStart?: string;
  recurringDateEnd?: string;
  shiftPricingType: ShiftPricingType;
  /**
   * Sent as a nested Money object, not the flattened
   * `hourlyRateAmount`/`hourlyRateCurrency` pair this type used to declare —
   * which is why applying a template never restored its rate.
   */
  hourlyRate?: Money | null;
  totalValue?: Money | null;
  /** How many people the saved shift needed. */
  candidatesCount: number;
  specialRequirements?: string;
  dressCode?: string;
  parkingInfo?: string;
  // Navigation Properties
  practice?: Practice | null;
  practiceBranch?: PracticeBranch | null;
  specialty?: Specialty | null;
  skills?: ShiftTemplateSkill[] | null;
  documents?: ShiftTemplateDocument[] | null;
  recurringWeekDays?: ShiftTemplateRecurringWeekDay[] | null;
  dates?: ShiftTemplateDate[] | null;
  location?: Address | null;
}

export interface ShiftTemplateRecurringWeekDay extends BaseEntity {
  shiftTemplateId: string;
  dayOfWeek: WeekDay;
  slots?: ShiftTemplateRecurringWeekDaySlot[] | null;
}

export interface ShiftTemplateRecurringWeekDaySlot extends BaseEntity {
  shiftTemplateRecurringWeekDayId: string;
  shiftTime: DateRange;
}

export interface ShiftTemplateDate extends BaseEntity {
  shiftTemplateId: string;
  shiftDate: Date;
  /** The day's envelope. `slots` is what it was derived from. */
  shiftTime: DateRange;
  slots?: ShiftTemplateDateSlot[] | null;
}

export interface ShiftTemplateDateSlot extends BaseEntity {
  shiftTemplateDateId: string;
  shiftTime: DateRange;
}

export interface ShiftTemplateSkill extends BaseEntity {
  shiftTemplateId: string;
  skillId: string;
  skill?: Skill | null;
  isRequired: boolean;
  yearsOfExperience?: number | null;
  notes?: string | null;
}

export interface ShiftTemplateDocument extends BaseEntity {
  shiftTemplateId: string;
  documentId: string;
  document?: UserDocument | null;
}

// Shift Entities
export interface Shift extends BaseEntity {
  practiceId: string;
  practiceBranchId: string;
  title: string;
  description: string;
  specialtyId: string;
  status: ShiftStatus;
  fromTemplate: boolean;
  shiftTemplateId?: string | null;
  // Time
  shiftTimeType: ShiftTimeType;
  recurringDateStart?: Date | null;
  recurringDateEnd?: Date | null;
  shiftPricingType: ShiftPricingType;
  hourlyRate: Money;
  totalValue?: Money;
  // Info
  candidatesCount: number;
  candidatesFulfilled: number;
  specialRequirements?: string | null;
  dressCode?: string | null;
  parkingInfo?: string | null;
  // Operations
  completedAt?: Date | null;
  completedBy?: string | null;
  completedByFullName?: string | null;
  cancelledAt?: Date | null;
  cancelledBy?: string | null;
  cancelledByFullName?: string | null;
  cancellationReason?: string | null;
  createdByFullName?: string | null;
  updatedByFullName?: string | null;
  // Navigation Properties
  practice?: Practice | null;
  practiceBranch?: PracticeBranch | null;
  specialty?: Specialty | null;
  shiftTemplate?: ShiftTemplate | null;
  recurringWeekDays?: ShiftRecurringWeekDay[];
  skills?: ShiftSkill[];
  documents?: ShiftDocument[];
  location?: Address | null;
  posts?: ShiftPost[];
  applications?: ShiftApplication[];
}

export interface ShiftRecurringWeekDay extends BaseEntity {
  shiftId: string;
  dayOfWeek: WeekDay;
  slots?: ShiftRecurringWeekDaySlot[] | null;
}

export interface ShiftRecurringWeekDaySlot extends BaseEntity {
  shiftRecurringWeekDayId: string;
  shiftTime: DateRange;
}

export interface ShiftSkill extends BaseEntity {
  shiftId: string;
  skillId: string;
  skill?: Skill | null;
  isRequired: boolean;
  yearsOfExperience?: number | null;
  notes?: string | null;
}

export interface ShiftDocument extends BaseEntity {
  shiftId: string;
  documentId: string;
  document?: UserDocument | null;
}

// Shift Post Entities
export interface ShiftPost extends BaseEntity {
  shiftId: string;
  practiceId: string;
  practiceBranchId: string;
  title: string;
  description: string;
  status: ShiftPostStatus;
  // Time
  shiftDate: Date;
  shiftTime: DateRange;
  breakHours: number;
  workingHours: number;
  totalHours: number;
  // Price
  shiftPricingType: ShiftPricingType;
  hourlyRate?: Money;
  totalValue?: Money;
  // Info
  specialRequirements?: string | null;
  dressCode?: string | null;
  parkingInfo?: string | null;
  // Operations
  completedAt?: Date;
  cancelledAt?: Date;
  cancelledBy?: string | null;
  cancellationReason?: string | null;
  // Navigation Properties
  shift?: Shift | null;
  practice?: Practice | null;
  practiceBranch?: PracticeBranch | null;
  slots?: ShiftPostSlot[] | null;
}

export interface ShiftPostSlot extends BaseEntity {
  shiftPostId: string;
  shiftTime: DateRange;
}

// Shift Applications — table shifts.shift_applications; unique (shift_id, professional_id).
export interface ShiftApplication extends BaseEntity {
  shiftId: string;
  professionalId: string;
  hourlyRate?: Money | null;
  notes?: string | null;
  isSelected: boolean;
  isNominated: boolean;
  isWithdrawn: boolean;
  appliedAt?: Date | null;
  nominatedAt?: Date | null;
  nominationAcceptedAt?: Date | null;
  selectedAt?: Date | null;
  withdrawnAt?: Date | null;
  status: ApplicationStatus;
}

export interface ShiftPostRecommendationRequest extends BaseEntity {
  shiftPostId: string;
  status: RecommendationRequestStatus;
  payload: Record<string, unknown>;
}

export interface ShiftPostRecommendation extends BaseEntity {
  recommendationRequestId: string;
  shiftPostId: string;
  professionalId: string;
  isDismissed: boolean;
  matchScore: number;
}

export interface ShiftPostRecommendationArchive extends BaseEntity {
  recommendationRequestId: string;
  shiftPostId: string;
  professionalId: string;
  isDismissed: boolean;
  matchScore: number;
}

export interface ProfessionalRecommendationRequest extends BaseEntity {
  tenantId: string;
  status: RecommendationRequestStatus;
  payload: Record<string, unknown>;
}

export interface ProfessionalRecommendation extends BaseEntity {
  recommendationRequestId: string;
  professionalId: string;
  shiftPostId: string;
  matchScore: number;
  isDismissed: boolean;
}

export interface ProfessionalRecommendationArchive extends BaseEntity {
  recommendationRequestId: string;
  professionalId: string;
  shiftPostId: string;
  matchScore: number;
  isDismissed: boolean;
}

// Request/Response Types
/** Matches backend PostShiftResponse(Guid ShiftId, ICollection<Guid> ShiftPostIds) */
export interface CreateShiftResponse {
  shiftId: string;
  shiftPostIds: string[];
}

/** Wire date for PostShift — calendar `yyyy-MM-dd`, not a UTC timestamp. */
export interface CreateShiftDateRequest {
  shiftDate: string;
  timeSlots: TimeSlot[];
}

/** @see Tempii.Application.Practices.PostShift.PostShiftRequest */
export interface CreateShiftRequest {
  practiceId: string;
  practiceBranchId: string;
  title: string;
  description: string;
  specialtyId: string;
  shiftTimeType: ShiftTimeType;
  recurringDateStart?: string;
  recurringDateEnd?: string;
  shiftPricingType: ShiftPricingType;
  hourlyRate?: Money;
  totalValue?: Money;
  specialRequirements?: string;
  dressCode?: string;
  parkingInfo?: string;
  candidatesCount?: number;
  shiftDates?: CreateShiftDateRequest[];
  recurringWeekDays?: RecurringWeekDayRequest[];
  documentIds?: string[];
  skillIds: string[];
}

/** Domain projection — `Tempii.Domain.Shifts.ShiftPostRecommendationRequest` (AI matching). */
export interface CreateShiftPostRecommendationRequest {
  shiftPostId: string;
  status?: RecommendationRequestStatus;
  payload: Record<string, unknown>;
}

export interface UpdateShiftPostRecommendationRequest
  extends Partial<CreateShiftPostRecommendationRequest> {
  id: string;
}

/** Domain projection — `Tempii.Domain.Shifts.ProfessionalRecommendationRequest`. */
export interface CreateProfessionalRecommendationRequest {
  tenantId: string;
  status?: RecommendationRequestStatus;
  payload: Record<string, unknown>;
}

export interface UpdateProfessionalRecommendationRequest
  extends Partial<CreateProfessionalRecommendationRequest> {
  id: string;
}

// Shift Applications API
/** @see Tempii.Application.Shifts.GetShiftApplications.GetShiftApplicationsQuery */
export interface GetShiftApplicationsRequest {
  shiftId?: string;
  professionalId?: string;
  status?: string;
  professionalIds?: string[];
}

/** Mirrors `GetShiftApplicationsResponse` (list query). `id` = shifts.shift_applications.id. */
/** @see Tempii.Application.Shifts.GetShiftApplications.GetShiftApplicationsResponse */
export interface GetShiftApplicationsResponseItem {
  id: string;
  shiftId: string;
  professionalId: string;
  hourlyRate: Money;
  notes?: string;
  isSelected: boolean;
  isNominated: boolean;
  isWithdrawn: boolean;
  appliedAt?: Date;
  nominatedAt?: Date;
  /** Set when the professional confirms the practice's nomination (required before practice Accept). */
  nominationAcceptedAt?: Date | null;
  selectedAt?: Date;
  withdrawnAt?: Date;
  status: ApplicationStatus;
  professionalName?: string;
  shiftTitle?: string;
  /** True when a Rate control on this row will be accepted rather than refused. */
  canRate: boolean;
  /** The viewer's own rating of this engagement, once they have left one. */
  myRatingId?: string | null;
}

export type GetShiftApplicationsResponse = GetShiftApplicationsResponseItem[];

/** Aligns with `Tempii.Application.Practices.SaveShiftTemplate.RecurringWeekDayRequest` (weekday + slots). */
export interface RecurringWeekDayRequest {
  dayOfWeek: WeekDay;
  timeSlots: TimeSlot[];
}

/** Aligns with `Tempii.Application.Practices.SaveShiftTemplate.ShiftDateRequest` (custom dates). */
export interface ShiftDateRequest {
  shiftDate: Date;
  timeSlots: TimeSlot[];
}

/**
 * Status icon source: SVG asset path for the design system status icons.
 * Used in ShiftTabTrigger and ShiftStatusChip.
 */
export const SHIFT_STATUS_ICON_PATHS: Record<
  Exclude<ShiftStatus, "deleted">,
  string
> = {
  active: "/assets/images/status-active.svg",
  upcoming: "/assets/images/status-upcoming.svg",
  completed: "/assets/images/status-completed.svg",
  cancelled: "/assets/images/status-cancelled.svg",
  draft: "/assets/images/status-draft.svg"
};

/**
 * Status colors extracted from design SVG assets.
 * Light/dark values for background and text in both themes.
 * Uses Tailwind arbitrary values (must be complete string literals for JIT).
 */
export type ShiftStatusColors = {
  /** Light mode: background color with alpha */
  bgLight: string;
  /** Light mode: text color */
  textLight: string;
  /** Dark mode: background color with alpha */
  bgDark: string;
  /** Dark mode: text color */
  textDark: string;
  /** Tailwind bg + text classes for chip (light and dark) */
  chipClasses: string;
  /** Tailwind tab + text + badge classes for ShiftTabTrigger */
  tabClasses: string;
  textClasses: string;
  badgeClasses: string;
  /** Tailwind card border classes for dashboard rows / mini stat wells */
  cardBorder: string;
};

/** Soft surface (bg only) extracted from chip classes for dashboard rows / mini stat wells. */
export function chipSurfaceBackgroundClasses(chipClasses: string): string {
  return chipClasses
    .split(/\s+/)
    .filter(
      (token) =>
        token.length > 0 &&
        (token.startsWith("bg-[") || token.startsWith("dark:bg-["))
    )
    .join(" ");
}

/** Left-border accent derived from badge solid-color classes (e.g. "bg-[#3B82F6]" → "border-l-[#3B82F6]"). */
export function cardBorderFromBadgeClasses(badgeClasses: string): string {
  const borderColors = badgeClasses
    .split(/\s+/)
    .filter((t) => t.length > 0)
    .map((t) => t.replace(/^(dark:)?bg-/, "$1border-l-"))
    .join(" ");
  return `border-l-4 ${borderColors}`;
}

/** Icon well, label pill, and status dot aligned with shift status chips (light + dark). */
export type DashboardStatusPillStyle = {
  dot: string;
  text: string;
  bg: string;
  cardBorder: string;
};

export function dashboardPillStyleFromStatusColors(
  c: ShiftStatusColors
): DashboardStatusPillStyle {
  return {
    dot: c.badgeClasses,
    text: c.textClasses,
    bg: chipSurfaceBackgroundClasses(c.chipClasses),
    cardBorder: cardBorderFromBadgeClasses(c.badgeClasses)
  };
}

export const SHIFT_STATUS_COLORS: Record<ShiftStatus, ShiftStatusColors> = {
  active: {
    bgLight: "#3B82F615",
    textLight: "#3B82F6",
    bgDark: "#60A5FA28",
    textDark: "#60A5FA",
    chipClasses:
      "bg-[#3B82F6]/10 text-[#3B82F6] dark:bg-[#60A5FA]/18 dark:text-[#60A5FA]",
    tabClasses:
      "!bg-[#3B82F6]/7 data-[state=active]:border-[#3B82F6] dark:!bg-[#60A5FA]/14 dark:data-[state=active]:!bg-[#60A5FA]/22 dark:data-[state=active]:border-[#60A5FA]",
    textClasses: "text-[#3B82F6] dark:text-[#60A5FA]",
    badgeClasses: "bg-[#3B82F6] dark:bg-[#3B82F6]",
    cardBorder: "border-l-4 border-l-blue-500",
  },
  cancelled: {
    bgLight: "#EF444415",
    textLight: "#EF4444",
    bgDark: "#F8717128",
    textDark: "#F87171",
    chipClasses:
      "bg-[#EF4444]/10 text-[#EF4444] dark:bg-[#F87171]/18 dark:text-[#F87171]",
    tabClasses:
      "!bg-[#EF4444]/7 data-[state=active]:border-[#EF4444] dark:!bg-[#F87171]/14 dark:data-[state=active]:!bg-[#F87171]/22 dark:data-[state=active]:border-[#F87171]",
    textClasses: "text-[#EF4444] dark:text-[#F87171]",
    badgeClasses: "bg-[#EF4444] dark:bg-[#EF4444]",
    cardBorder: "border-l-4 border-l-red-500",
  },
  completed: {
    bgLight: "#10B98115",
    textLight: "#10B981",
    bgDark: "#34D39928",
    textDark: "#34D399",
    chipClasses:
      "bg-[#10B981]/10 text-[#10B981] dark:bg-[#34D399]/18 dark:text-[#34D399]",
    tabClasses:
      "!bg-[#10B981]/7 data-[state=active]:border-[#10B981] dark:!bg-[#34D399]/14 dark:data-[state=active]:!bg-[#34D399]/22 dark:data-[state=active]:border-[#34D399]",
    textClasses: "text-[#10B981] dark:text-[#34D399]",
    badgeClasses: "bg-[#10B981] dark:bg-[#10B981]",
    cardBorder: "border-l-4 border-l-green-500",
  },
  draft: {
    bgLight: "#64748B15",
    textLight: "#64748B",
    bgDark: "#94A3B828",
    textDark: "#94A3B8",
    chipClasses:
      "bg-[#64748B]/10 text-[#64748B] dark:bg-[#94A3B8]/18 dark:text-[#94A3B8]",
    tabClasses:
      "!bg-[#64748B]/7 data-[state=active]:border-[#64748B] dark:!bg-[#94A3B8]/14 dark:data-[state=active]:!bg-[#94A3B8]/22 dark:data-[state=active]:border-[#CBD5E1]",
    textClasses: "text-[#64748B] dark:text-[#CBD5E1]",
    badgeClasses: "bg-[#64748B] dark:bg-[#94A3B8]",
    cardBorder: "border-l-4 border-l-gray-500",
  },
  upcoming: {
    bgLight: "#F59E0B15",
    textLight: "#F59E0B",
    bgDark: "#FBBF2428",
    textDark: "#FBBF24",
    chipClasses:
      "bg-[#F59E0B]/10 text-[#F59E0B] dark:bg-[#FBBF24]/18 dark:text-[#FBBF24]",
    tabClasses:
      "!bg-[#F59E0B]/7 data-[state=active]:border-[#F59E0B] dark:!bg-[#FBBF24]/14 dark:data-[state=active]:!bg-[#FBBF24]/22 dark:data-[state=active]:border-[#FBBF24]",
    textClasses: "text-[#F59E0B] dark:text-[#FBBF24]",
    badgeClasses: "bg-[#F59E0B] dark:bg-[#F59E0B]",
    cardBorder: "border-l-4 border-l-yellow-500",
  },
  deleted: {
    bgLight: "#DC262615",
    textLight: "#DC2626",
    bgDark: "#F8717128",
    textDark: "#F87171",
    chipClasses:
      "bg-[#DC2626]/10 text-[#DC2626] dark:bg-[#F87171]/18 dark:text-[#F87171]",
    tabClasses:
      "!bg-[#DC2626]/7 data-[state=active]:border-[#DC2626] dark:!bg-[#F87171]/14 dark:data-[state=active]:!bg-[#F87171]/22 dark:data-[state=active]:border-[#F87171]",
    textClasses: "text-[#DC2626] dark:text-[#F87171]",
    badgeClasses: "bg-[#DC2626] dark:bg-[#DC2626]",
    cardBorder: "border-l-4 border-l-red-500",
  }
};

/**
 * Icon component per status. Uses existing icon-utils for consistency.
 * For SVG assets, use SHIFT_STATUS_ICON_PATHS with <img>.
 */
export const SHIFT_STATUS_ICONS: Record<
  ShiftStatus,
  React.ComponentType<{ className?: string }>
> = {
  active: ActiveIcon,
  upcoming: UrgentIcon,
  completed: CheckBoldIcon,
  cancelled: CrossBoldIcon,
  draft: EditDraftBoldIcon,
  deleted: TrashIcon
};

// Shift Document API Types
/** @see Tempii.Application.Shifts.UploadShiftDocument.UploadShiftDocumentCommand */
export interface UploadShiftDocumentRequest {
  practiceId: string;
  shiftId: string;
  formData: FormData;
}

export type UploadShiftDocumentResponse = ShiftDocument;

/** @see Tempii.Application.Shifts.RemoveShiftDocument.RemoveShiftDocumentCommand */
export interface RemoveShiftDocumentRequest {
  practiceId: string;
  shiftId: string;
  documentId: string;
}

export type RemoveShiftDocumentResponse = string;

// Shift Template Document API Types
/** @see Tempii.Application.Shifts.UploadShiftTemplateDocument.UploadShiftTemplateDocumentCommand */
export interface UploadShiftTemplateDocumentRequest {
  practiceId: string;
  templateId: string;
  formData: FormData;
}

export type UploadShiftTemplateDocumentResponse = ShiftTemplateDocument;

/** @see Tempii.Application.Shifts.RemoveShiftTemplateDocument.RemoveShiftTemplateDocumentCommand */
export interface RemoveShiftTemplateDocumentRequest {
  practiceId: string;
  templateId: string;
  documentId: string;
}

export type RemoveShiftTemplateDocumentResponse = string;

